"use client";

import { SEARCH_MENTIONS } from "@/api/apollo-client/queries/Queries";
import { PostType } from "@/constants";
import { useQuery } from "@apollo/client";
import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import toast from "react-hot-toast";

export type Mentions = {
  id: string;
  name: string;
  profile_picture?: ProfilePicture;
};

type ProfilePicture = {
  url: string;
};

export default function AssociationWith({
  onSelect,
  selected,
  postType,
}: {
  onSelect: (mention: Mentions[]) => void;
  selected: Mentions[];
  postType: PostType;
}) {
  const [input, setInput] = useState("");
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [showMentionList, setShowMentionList] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, width: 0 });

  const inputRef = useRef<HTMLInputElement>(null);

  // Detect @ typing
  useEffect(() => {
    // const match = input.match(/@(\w*)$/);
    if (input) {
      setMentionQuery(input); // can be ''
      setShowMentionList(true);
    } else {
      setMentionQuery(null);
      setShowMentionList(false);
    }
  }, [input]);

  // Query users
  const { data, loading, error } = useQuery(SEARCH_MENTIONS, {
    variables: {
      name_prefix: mentionQuery || "",
      first: 5,
    },
    skip: mentionQuery === null,
  });

  const results: Mentions[] = data?.searchMentioned || [];

  // Get popup position below input
  useEffect(() => {
    if (inputRef.current && showMentionList) {
      const rect = inputRef.current.getBoundingClientRect();
      setPopupPos({
        top: rect.bottom + 4, // popup below input
        left: rect.left,
        width: rect.width,
      });
    }
  }, [showMentionList, input]);

  const handleSelect = (mention: Mentions) => {
    const alreadyExists = selected.some(
      (item) => item.name.toLowerCase() === mention.name.toLowerCase()
    );

    if (alreadyExists) {
      toast.error("This person is already added.");
      return;
    }
    if (!inputRef.current) return;
    const updated = input.replace(/@(\w*)$/, `@${mention.name} `);
    setInput(updated);
    setShowMentionList(false);

    // Create a new array instead of mutating props
    const updatedSelected = [...selected, mention];
    onSelect(updatedSelected);
    setInput("");
  };

  useEffect(() => {
    setInput("");
    //it will clear the selected mentions when post type is changed,so for now we are commenting it out
    // setMentionQuery(null);
    // setShowMentionList(false);
    // onSelect([]);
  }, [postType]);

  return (
    <>
      {/* Input field */}
      <div>
        <div className=" relative w-full max-w-md ">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="@mention to link post with them"
            className="w-full py-1 border rounded focus:outline-none relative inline-flex items-center gap-2 px-3 py-1 border rounded-full transition"
          />
          {showMentionList && (
            <div className="fixed bg-gray-800 border shadow-lg rounded max-h-60 overflow-y-auto z-50   max-w-56">
              {loading && (
                <div className="p-2 text-sm text-gray-500">Loading...</div>
              )}
              {error && (
                <div className="p-2 text-sm text-red-500">
                  Error fetching mentions
                </div>
              )}
              {results.length === 0 && !loading ? (
                <div className="p-2 text-sm text-gray-400">No results</div>
              ) : (
                results.map((item) => (
                  <div
                    key={item.id}
                    className="px-4 py-2 hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                    onClick={() => handleSelect(item)}
                  >
                    {item.profile_picture && (
                      <Image
                        src={item.profile_picture.url}
                        alt={item.name}
                        width={200}
                        height={200}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    )}
                    <span className="text-white">{item.name}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Floating Popup (below input) */}
      </div>
    </>
  );
}
